<script>
import Layout from "../../../layouts/main";
import PageHeader from "@/components/page-header";
import SelectWithSearch from "@/views/pages/Dilfoods/Common/SelectWithSearch.vue";
import { RestaurantsPath, HygieneChecklistPath } from "../../../../path";
import axios from "axios";

export default {
  components: {
    Layout,
    PageHeader,
    SelectWithSearch,
  },
  data() {
    return {
      title: "Daily Hygiene Checklist",
      items: [
        {
          text: "Kitchen Management",
        },
        {
          text: "Daily Hygiene Checklist",
          active: true,
        },
      ],
      base_url: process.env.VUE_APP_API_BACKEND_URL,
      cdn: process.env.VUE_APP_AWS_CDN,
      currentPage: 1,
      viewMode: "calendar", // 'calendar' or 'restaurant'
      loading: false,
      selectedMonth: new Date().getMonth(),
      selectedYear: new Date().getFullYear(),
      selectedDayDetails: null,
      showDayDetailsModal: false,
      showImageModal: false,
      currentImages: [],
      currentImageTitle: "",

      // Calendar view options
      calendarViewMode: "all", // "all" or "recorded"

      // Restaurant search
      selectedRestaurant: null,

      // Restaurant view month selection
      restaurantSelectedMonth: new Date().getMonth(),
      restaurantSelectedYear: new Date().getFullYear(),
      restaurantViewMode: "last30", // "last30" or "month"

      // API data for calendar view
      calendarData: {},
      calendarSummary: {
        total_records: 0,
        completed_records: 0,
        completion_percentage: 0,
        total_restaurants: 0,
      },

      // API data for restaurant timeline
      restaurantData: [],
      restaurantPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      },

      // API loading states
      calendarLoading: false,
      restaurantLoading: false,
    };
  },

  computed: {
    currentMonthDays() {
      const year = this.selectedYear;
      const month = this.selectedMonth;
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const firstDay = new Date(year, month, 1).getDay();

      const days = [];

      // Add empty cells for days before the first day of the month
      for (let i = 0; i < firstDay; i++) {
        days.push(null);
      }

      // Add days of the month
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(
          day
        ).padStart(2, "0")}`;
        const dayData = this.calendarData.daily_data?.[dateStr] || {
          opened: 0,
          completed: 0,
          partially_completed: 0,
        };

        let completionPercentage = 0;
        if (this.calendarViewMode === "recorded") {
          // Calculate based on restaurants that opened forms
          completionPercentage =
            dayData.opened > 0
              ? Math.round((dayData.completed / dayData.opened) * 100)
              : 0;
        } else {
          // Calculate based on all restaurants
          completionPercentage = Math.round(
            (dayData.completed /
              (this.calendarSummary.total_restaurants || 1)) *
              100
          );
        }

        days.push({
          day,
          date: dateStr,
          opened: dayData.opened,
          completed: dayData.completed,
          partiallyCompleted: dayData.partially_completed,
          completionPercentage,
        });
      }

      return days;
    },

    monthNames() {
      return [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
    },

    paginatedRestaurants() {
      let filteredData = this.restaurantData;

      if (this.selectedRestaurant) {
        filteredData = this.restaurantData.filter(
          (r) => r.restaurant_id === this.selectedRestaurant.id
        );
      }

      // Process each restaurant to ensure all days are shown
      return filteredData.map((restaurant) => {
        const processedRestaurant = { ...restaurant };
        processedRestaurant.timeline = this.generateRestaurantTimeline(
          restaurant.timeline || []
        );
        return processedRestaurant;
      });
    },

    RestaurantsPath() {
      return RestaurantsPath;
    },
  },

  methods: {
    async fetchCalendarData() {
      this.calendarLoading = true;
      try {
        const params = {
          month: this.selectedMonth + 1, // Convert to 1-based month
          year: this.selectedYear,
        };

        const response = await axios.get(
          this.base_url + HygieneChecklistPath.GET_CALENDAR_DATA,
          { params }
        );

        if (response.data.status) {
          this.calendarData = response.data.data;
          this.calendarSummary = {
            total_records: response.data.data.total_records,
            completed_records: response.data.data.completed_records,
            completion_percentage: response.data.data.completion_percentage,
            total_restaurants: response.data.data.total_restaurants,
          };
        }
      } catch (error) {
        console.error("Error fetching calendar data:", error);
        this.$bvToast.toast("Error loading calendar data", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.calendarLoading = false;
      }
    },

    async fetchRestaurantData() {
      this.restaurantLoading = true;
      try {
        const params = {
          page: this.currentPage,
          per_page: 10,
        };

        if (this.restaurantViewMode === "last30") {
          params.days = 30;
        } else {
          params.month = this.restaurantSelectedMonth + 1;
          params.year = this.restaurantSelectedYear;
        }

        if (this.selectedRestaurant) {
          params.search = this.selectedRestaurant.name;
        }

        const response = await axios.get(
          this.base_url + HygieneChecklistPath.GET_RESTAURANT_DATA,
          { params }
        );

        if (response.data.status) {
          this.restaurantData = response.data.data.data;
          this.restaurantPagination = {
            current_page: response.data.data.current_page,
            last_page: response.data.data.last_page,
            per_page: response.data.data.per_page,
            total: response.data.data.total,
          };
        }
      } catch (error) {
        console.error("Error fetching restaurant data:", error);
        this.$bvToast.toast("Error loading restaurant data", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.restaurantLoading = false;
      }
    },

    generateRestaurantTimeline(existingTimeline) {
      const timeline = [];
      let startDate, endDate;

      if (this.restaurantViewMode === "last30") {
        // Last 30 days from today
        endDate = new Date();
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 29); // 30 days including today
      } else {
        // Specific month
        startDate = new Date(
          this.restaurantSelectedYear,
          this.restaurantSelectedMonth,
          1
        );
        endDate = new Date(
          this.restaurantSelectedYear,
          this.restaurantSelectedMonth + 1,
          0
        );
      }

      // Create a map of existing timeline data for quick lookup
      const existingDataMap = {};
      existingTimeline.forEach((day) => {
        existingDataMap[day.date] = day;
      });

      // Generate all days in the range
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split("T")[0];
        const dayNumber = currentDate.getDate();

        if (existingDataMap[dateStr]) {
          // Use existing data
          timeline.push({
            ...existingDataMap[dateStr],
            dayNumber,
          });
        } else {
          // Create empty day data
          timeline.push({
            date: dateStr,
            dayNumber,
            opened: 0,
            completed: 0,
            completion_percentage: 0,
            is_completed: false,
            oos_count: 0,
            completed_at: null,
            oos_images: [],
            pre_preparation_images: [],
            packaging_images: [],
            freezer_image: null,
            chiller_image: null,
          });
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      return timeline;
    },

    getDayCompletionClass(completionPercentage) {
      if (completionPercentage === 0) return "completion-none";
      if (completionPercentage < 25) return "completion-very-low";
      if (completionPercentage < 50) return "completion-low";
      if (completionPercentage < 80) return "completion-medium";
      return "completion-high";
    },

    getCircleCompletionClass(completionPercentage) {
      if (completionPercentage === 0) return "circle-none";
      if (completionPercentage < 25) return "circle-very-low";
      if (completionPercentage < 50) return "circle-low";
      if (completionPercentage < 80) return "circle-medium";
      return "circle-high";
    },

    async onDayClick(dayData) {
      if (!dayData) return;

      this.showDayDetailsModal = true;
      this.selectedDayDetails = null; // Optionally show a loading state

      try {
        // Extract year, month, day from the date string (YYYY-MM-DD)
        const [year, month, day] = dayData.date.split("-").map(Number);

        const response = await axios.get(
          this.base_url + HygieneChecklistPath.GET_CALENDAR_LIST,
          { params: { day, month, year } }
        );

        if (response.data.status) {
          this.selectedDayDetails = {
            date: response.data.data.date,
            completedRestaurants: (
              response.data.data.completed_restaurants || []
            ).map((r) => ({
              restaurant: r.restaurant_name,
              records: [r.record],
            })),
            partiallyCompletedRestaurants: (
              response.data.data.partially_completed_restaurants || []
            ).map((r) => ({
              restaurant: r.restaurant_name,
              records: [r.record],
            })),
            summary: {
              opened: dayData.opened,
              completed: dayData.completed,
              partiallyCompleted: dayData.partiallyCompleted,
              completionPercentage: dayData.completionPercentage,
            },
          };
        } else {
          this.selectedDayDetails = {
            date: dayData.date,
            completedRestaurants: [],
            partiallyCompletedRestaurants: [],
            summary: {
              opened: dayData.opened,
              completed: dayData.completed,
              partiallyCompleted: dayData.partiallyCompleted,
              completionPercentage: dayData.completionPercentage,
            },
          };
        }
      } catch (error) {
        this.$bvToast.toast("Error loading day details", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
        this.showDayDetailsModal = false;
      }
    },

    onCircleClick(restaurant, dayData) {
      if (!dayData) return;

      this.selectedDayDetails = {
        date: dayData.date,
        restaurant: restaurant.restaurant_name,
        singleRecord: dayData, // Show timeline data
        isRestaurantView: true,
      };

      this.showDayDetailsModal = true;
    },

    changeMonth(direction) {
      if (direction === "prev") {
        if (this.selectedMonth === 0) {
          this.selectedMonth = 11;
          this.selectedYear--;
        } else {
          this.selectedMonth--;
        }
      } else {
        if (this.selectedMonth === 11) {
          this.selectedMonth = 0;
          this.selectedYear++;
        } else {
          this.selectedMonth++;
        }
      }

      // Fetch new calendar data for the new month
      this.fetchCalendarData();
    },

    changeRestaurantMonth(direction) {
      if (direction === "prev") {
        if (this.restaurantSelectedMonth === 0) {
          this.restaurantSelectedMonth = 11;
          this.restaurantSelectedYear--;
        } else {
          this.restaurantSelectedMonth--;
        }
      } else {
        if (this.restaurantSelectedMonth === 11) {
          this.restaurantSelectedMonth = 0;
          this.restaurantSelectedYear++;
        } else {
          this.restaurantSelectedMonth++;
        }
      }

      // Fetch new restaurant data for the new month
      this.fetchRestaurantData();
    },

    formatDateForDisplay(dateStr) {
      return new Date(dateStr).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    exportData() {
      this.$bvToast.toast("Export functionality will be implemented", {
        title: "Info",
        variant: "info",
        solid: true,
      });
    },

    getStatusVariant(isCompleted) {
      return isCompleted ? "success" : "warning";
    },

    getStatusText(isCompleted) {
      return isCompleted ? "Completed" : "Pending";
    },

    formatDateTime(datetime) {
      if (!datetime) return "N/A";
      return new Date(datetime).toLocaleString();
    },

    selectRestaurant(restaurant) {
      this.selectedRestaurant = restaurant;
      this.currentPage = 1; // Reset to first page
      this.fetchRestaurantData();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchRestaurantData();
    },

    viewImageLarge(images, title) {
      if (typeof images === "object" && !Array.isArray(images)) {
        this.currentImages = images;
      } else if (Array.isArray(images)) {
        this.currentImages = images;
      } else {
        this.currentImages = [];
      }
      this.currentImageTitle = title;
      this.showImageModal = true;
    },
  },

  mounted() {
    this.fetchCalendarData();
    this.fetchRestaurantData();
  },

  watch: {
    viewMode() {
      if (this.viewMode === "calendar") {
        this.fetchCalendarData();
      } else {
        this.fetchRestaurantData();
      }
    },

    calendarViewMode() {
      if (this.viewMode === "calendar") {
        this.fetchCalendarData();
      }
    },

    restaurantViewMode() {
      if (this.viewMode === "restaurant") {
        this.fetchRestaurantData();
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- View Mode Toggle -->
            <div class="row mb-4">
              <div class="col-md-3">
                <label class="form-label">View Mode</label>
                <b-form-select
                  v-model="viewMode"
                  :options="[
                    {
                      value: 'calendar',
                      text: 'Calendar View (Global Overview)',
                    },
                    { value: 'restaurant', text: 'Restaurant View (Timeline)' },
                  ]"
                ></b-form-select>
              </div>

              <!-- Calendar View Mode Buttons -->
              <div class="col-md-6" v-if="viewMode === 'calendar'">
                <div class="form-group">
                  <label class="form-label">Calendar Filter</label>
                  <div class="btn-group d-flex" role="group">
                    <b-button
                      class="me-2"
                      :variant="
                        calendarViewMode === 'all'
                          ? 'primary'
                          : 'outline-primary'
                      "
                      @click="calendarViewMode = 'all'"
                    >
                      Show by All Restaurants
                    </b-button>
                    <b-button
                      :variant="
                        calendarViewMode === 'recorded'
                          ? 'primary'
                          : 'outline-primary'
                      "
                      @click="calendarViewMode = 'recorded'"
                    >
                      Show by Recorded Restaurants
                    </b-button>
                  </div>
                </div>
              </div>

              <!-- Restaurant Search -->
              <div class="col-md-3" v-if="viewMode !== 'calendar'">
                <label class="form-label">Search Restaurant</label>
                <select-with-search
                  :search_api="RestaurantsPath.GET_RESTAURANTS"
                  selected_by="id"
                  track_by="id"
                  display="name"
                  @selectedOption="selectRestaurant"
                  placeholder="Select a restaurant..."
                ></select-with-search>
              </div>

              <!-- Restaurant View Mode -->
              <div class="col-md-3" v-if="viewMode !== 'calendar'">
                <label class="form-label">Time Period</label>
                <b-form-select
                  v-model="restaurantViewMode"
                  :options="[
                    { value: 'last30', text: 'Last 30 Days' },
                    { value: 'month', text: 'By Month' },
                  ]"
                ></b-form-select>
              </div>

              <div
                class="col-md-2 d-flex align-items-end"
                v-if="selectedRestaurant && viewMode !== 'calendar'"
              >
                <label class="form-label"></label>
                <b-button
                  variant="outline-secondary"
                  @click="selectedRestaurant = null"
                >
                  Clear Filter
                </b-button>
              </div>

              <!-- <div
                class="col-md-2 d-flex align-items-end"
                v-if="viewMode === 'restaurant'"
              >
                <b-button variant="success" @click="exportData()">
                  Export Data
                </b-button>
              </div> -->
            </div>

            <!-- Loading Spinner -->
            <div
              v-if="calendarLoading || restaurantLoading"
              class="text-center py-4"
            >
              <b-spinner variant="primary"></b-spinner>
              <p class="mt-2">Loading...</p>
            </div>

            <!-- Calendar View (Global Overview) -->
            <div v-else-if="viewMode === 'calendar'" class="calendar-view">
              <!-- Month Navigation -->
              <div class="month-navigation mb-4">
                <div class="d-flex justify-content-between align-items-center">
                  <b-button
                    variant="outline-primary"
                    @click="changeMonth('prev')"
                  >
                    <i class="fa fa-chevron-left"></i> Previous
                  </b-button>

                  <h4 class="mb-0">
                    {{ monthNames[selectedMonth] }} {{ selectedYear }}
                  </h4>

                  <b-button
                    variant="outline-primary"
                    @click="changeMonth('next')"
                  >
                    Next <i class="fa fa-chevron-right"></i>
                  </b-button>
                </div>
              </div>

              <!-- Calendar Grid -->
              <div class="calendar-grid">
                <!-- Day Headers -->
                <div class="calendar-header">
                  <div class="day-header">Sun</div>
                  <div class="day-header">Mon</div>
                  <div class="day-header">Tue</div>
                  <div class="day-header">Wed</div>
                  <div class="day-header">Thu</div>
                  <div class="day-header">Fri</div>
                  <div class="day-header">Sat</div>
                </div>

                <!-- Calendar Days -->
                <div class="calendar-body">
                  <div
                    v-for="(day, index) in currentMonthDays"
                    :key="index"
                    class="calendar-day"
                    :class="[
                      day
                        ? getDayCompletionClass(day.completionPercentage)
                        : 'empty-day',
                      day && day.opened > 0 ? 'clickable' : '',
                    ]"
                    @click="day && onDayClick(day)"
                  >
                    <div v-if="day" class="day-content">
                      <div class="day-number">{{ day.day }}</div>
                      <div class="day-stats" v-if="day.opened > 0">
                        <div class="completion-percentage">
                          {{ day.completionPercentage }}%
                        </div>
                        <div class="record-count">
                          {{ day.completed }} / {{ day.opened }} /
                          {{ calendarSummary.total_restaurants }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Legend -->
              <div class="calendar-legend mt-4">
                <h6>Completion Legend:</h6>
                <div class="legend-items">
                  <div class="legend-item">
                    <div class="legend-color completion-none"></div>
                    <span>No Records (0%)</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color completion-very-low"></div>
                    <span>Very Low (1-24%)</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color completion-low"></div>
                    <span>Low (25-49%)</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color completion-medium"></div>
                    <span>Medium (50-79%)</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-color completion-high"></div>
                    <span>High (80-100%)</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Restaurant View (Timeline) -->
            <div v-else-if="viewMode === 'restaurant'" class="restaurant-view">
              <!-- Month Navigation for Restaurant View -->
              <div
                class="month-navigation mb-4"
                v-if="restaurantViewMode === 'month'"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <b-button
                    variant="outline-primary"
                    @click="changeRestaurantMonth('prev')"
                  >
                    <i class="fa fa-chevron-left"></i> Previous
                  </b-button>

                  <h4 class="mb-0">
                    {{ monthNames[restaurantSelectedMonth] }}
                    {{ restaurantSelectedYear }}
                  </h4>

                  <b-button
                    variant="outline-primary"
                    @click="changeRestaurantMonth('next')"
                  >
                    Next <i class="fa fa-chevron-right"></i>
                  </b-button>
                </div>
              </div>

              <div class="restaurant-timeline">
                <div
                  v-for="restaurant in paginatedRestaurants"
                  :key="restaurant.restaurant_id"
                  class="restaurant-row"
                >
                  <div class="restaurant-info">
                    <h6 class="restaurant-name">
                      {{ restaurant.restaurant_name }}
                    </h6>
                    <small class="text-muted"
                      >ID: {{ restaurant.restaurant_id }}</small
                    >
                  </div>

                  <div class="timeline-circles">
                    <div
                      v-for="(day, index) in restaurant.timeline"
                      :key="index"
                      class="timeline-circle"
                      :class="
                        getCircleCompletionClass(day.completion_percentage)
                      "
                      @click="onCircleClick(restaurant, day)"
                      v-b-tooltip:hover
                      :title="
                        day.opened > 0
                          ? `${day.date}: ${day.completion_percentage}% (${day.completed}/${day.opened})`
                          : `${day.date}: No records`
                      "
                    >
                      <span class="circle-date">{{ day.dayNumber }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pagination for Restaurant View -->
              <div class="row mt-4" v-if="restaurantPagination.last_page > 1">
                <div class="col">
                  <div
                    class="dataTables_paginate paging_simple_numbers float-right"
                  >
                    <ul class="pagination pagination-rounded mb-0">
                      <b-pagination
                        v-model="currentPage"
                        :total-rows="restaurantPagination.total"
                        :per-page="restaurantPagination.per_page"
                        @change="onPageChange"
                      ></b-pagination>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Timeline Legend -->
              <div class="timeline-legend mt-4">
                <h6>
                  Timeline Legend ({{
                    restaurantViewMode === "last30"
                      ? "Last 30 Days"
                      : monthNames[restaurantSelectedMonth] +
                        " " +
                        restaurantSelectedYear
                  }}):
                </h6>
                <div class="legend-items">
                  <div class="legend-item">
                    <div class="legend-circle circle-none"></div>
                    <span>No Records (0%)</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-circle circle-very-low"></div>
                    <span>Very Low (1-24%)</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-circle circle-low"></div>
                    <span>Low (25-49%)</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-circle circle-medium"></div>
                    <span>Medium (50-79%)</span>
                  </div>
                  <div class="legend-item">
                    <div class="legend-circle circle-high"></div>
                    <span>High (80-100%)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Day Details Modal -->
    <b-modal
      v-model="showDayDetailsModal"
      :title="
        selectedDayDetails ? formatDateForDisplay(selectedDayDetails.date) : ''
      "
      size="xl"
      hide-footer
    >
      <div v-if="selectedDayDetails">
        <!-- Calendar View Modal (Show completed and partially completed restaurants) -->
        <div v-if="!selectedDayDetails.isRestaurantView">
          <!-- Summary -->
          <div class="row mb-4">
            <div class="col-md-12">
              <div class="alert alert-info">
                <h6 class="mb-2">
                  Summary for
                  {{ formatDateForDisplay(selectedDayDetails.date) }}
                </h6>
                <div class="row">
                  <div class="col-md-4">
                    <strong>Total Records:</strong>
                    {{ selectedDayDetails.summary.opened }}
                  </div>
                  <div class="col-md-4">
                    <strong>Completed:</strong>
                    {{ selectedDayDetails.summary.completed }}
                  </div>
                  <div class="col-md-4">
                    <strong>Completion Rate:</strong>
                    {{ selectedDayDetails.summary.completionPercentage }}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Completed Restaurants -->
          <div
            v-if="selectedDayDetails.completedRestaurants.length > 0"
            class="mb-4"
          >
            <h6 class="text-success">
              Completed Restaurants ({{
                selectedDayDetails.completedRestaurants.length
              }})
            </h6>
            <div class="table-responsive">
              <b-table
                :items="selectedDayDetails.completedRestaurants"
                :fields="[
                  { key: 'restaurant', label: 'Restaurant' },
                  { key: 'oos_count', label: 'OOS Count' },
                  {
                    key: 'completion_percentage',
                    label: 'Completion Percentage',
                  },
                  { key: 'actions', label: 'Actions' },
                ]"
                responsive="sm"
              >
                <template v-slot:cell(oos_count)="row">
                  {{ row.item.records[0].oos_count || 0 }}
                </template>
                <template v-slot:cell(completion_percentage)="row">
                  {{ row.item.records[0].completion_percentage || 0 }}
                </template>
                <template v-slot:cell(actions)="row">
                  <b-button
                    variant="info"
                    size="sm"
                    @click="
                      viewImageLarge(
                        {
                          oos_images: row.item.records[0].oos_images || [],
                          pre_preparation_images:
                            row.item.records[0].pre_preparation_images || [],
                          packaging_images:
                            row.item.records[0].packaging_images || [],
                          freezer_image: row.item.records[0].freezer_image
                            ? [row.item.records[0].freezer_image]
                            : [],
                          chiller_image: row.item.records[0].chiller_image
                            ? [row.item.records[0].chiller_image]
                            : [],
                        },
                        'All Images'
                      )
                    "
                  >
                    <i class="fa fa-image"></i> View All Images
                  </b-button>
                </template>
              </b-table>
            </div>
          </div>

          <!-- Partially Completed Restaurants -->
          <div
            v-if="selectedDayDetails.partiallyCompletedRestaurants.length > 0"
          >
            <h6 class="text-warning">
              Partially Completed Restaurants ({{
                selectedDayDetails.partiallyCompletedRestaurants.length
              }})
            </h6>
            <div class="table-responsive">
              <b-table
                :items="selectedDayDetails.partiallyCompletedRestaurants"
                :fields="[
                  { key: 'restaurant', label: 'Restaurant' },
                  { key: 'oos_count', label: 'OOS Count' },
                  {
                    key: 'completion_percentage',
                    label: 'Completion Percentage',
                  },
                  { key: 'actions', label: 'Actions' },
                ]"
                responsive="sm"
              >
                <template v-slot:cell(oos_count)="row">
                  {{ row.item.records[0].oos_count || 0 }}
                </template>
                <template v-slot:cell(completion_percentage)="row">
                  {{ row.item.records[0].completion_percentage || 0 }}
                </template>
                <template v-slot:cell(actions)="row">
                  <b-button
                    variant="info"
                    size="sm"
                    @click="
                      viewImageLarge(
                        {
                          oos_images: row.item.records[0].oos_images || [],
                          pre_preparation_images:
                            row.item.records[0].pre_preparation_images || [],
                          packaging_images:
                            row.item.records[0].packaging_images || [],
                          freezer_image: row.item.records[0].freezer_image
                            ? [row.item.records[0].freezer_image]
                            : [],
                          chiller_image: row.item.records[0].chiller_image
                            ? [row.item.records[0].chiller_image]
                            : [],
                        },
                        'All Images'
                      )
                    "
                  >
                    <i class="fa fa-image"></i> View All Images
                  </b-button>
                </template>
              </b-table>
            </div>
          </div>
        </div>

        <!-- Restaurant View Modal (Show single record data) -->
        <div v-else>
          <div class="row">
            <div class="col-md-6">
              <h6>Restaurant Details</h6>
              <table class="table table-borderless">
                <tr>
                  <td><strong>Restaurant:</strong></td>
                  <td>{{ selectedDayDetails.restaurant }}</td>
                </tr>
                <tr>
                  <td><strong>Date:</strong></td>
                  <td>{{ formatDateForDisplay(selectedDayDetails.date) }}</td>
                </tr>
                <tr>
                  <td><strong>OOS Count:</strong></td>
                  <td>{{ selectedDayDetails.singleRecord.oos_count || 0 }}</td>
                </tr>
                <tr>
                  <td><strong>Status:</strong></td>
                  <td>
                    <b-badge
                      :variant="
                        getStatusVariant(
                          selectedDayDetails.singleRecord.is_completed
                        )
                      "
                    >
                      {{
                        getStatusText(
                          selectedDayDetails.singleRecord.is_completed
                        )
                      }}
                    </b-badge>
                  </td>
                </tr>
                <tr v-if="selectedDayDetails.singleRecord.completed_at">
                  <td><strong>Completed At:</strong></td>
                  <td>
                    {{
                      formatDateTime(
                        selectedDayDetails.singleRecord.completed_at
                      )
                    }}
                  </td>
                </tr>
              </table>
            </div>

            <div class="col-md-6">
              <h6>Images</h6>
              <div class="image-actions">
                <b-button
                  variant="info"
                  size="sm"
                  @click="
                    viewImageLarge(
                      selectedDayDetails.singleRecord.oos_images,
                      'OOS Images'
                    )
                  "
                  class="me-2 mb-2"
                  v-if="
                    selectedDayDetails.singleRecord.oos_images &&
                    selectedDayDetails.singleRecord.oos_images.length > 0
                  "
                >
                  <i class="fa fa-image"></i> View OOS Images ({{
                    selectedDayDetails.singleRecord.oos_images.length
                  }})
                </b-button>
                <b-button
                  variant="info"
                  size="sm"
                  @click="
                    viewImageLarge(
                      selectedDayDetails.singleRecord.pre_preparation_images,
                      'Pre-preparation Images'
                    )
                  "
                  class="me-2 mb-2"
                  v-if="
                    selectedDayDetails.singleRecord.pre_preparation_images &&
                    selectedDayDetails.singleRecord.pre_preparation_images
                      .length > 0
                  "
                >
                  <i class="fa fa-image"></i> View Pre-prep Images ({{
                    selectedDayDetails.singleRecord.pre_preparation_images
                      .length
                  }})
                </b-button>
                <b-button
                  variant="info"
                  size="sm"
                  @click="
                    viewImageLarge(
                      selectedDayDetails.singleRecord.packaging_images,
                      'Packaging Images'
                    )
                  "
                  class="me-2 mb-2"
                  v-if="
                    selectedDayDetails.singleRecord.packaging_images &&
                    selectedDayDetails.singleRecord.packaging_images.length > 0
                  "
                >
                  <i class="fa fa-image"></i> View Packaging Images ({{
                    selectedDayDetails.singleRecord.packaging_images.length
                  }})
                </b-button>
                <b-button
                  variant="info"
                  size="sm"
                  @click="
                    viewImageLarge(
                      [selectedDayDetails.singleRecord.freezer_image],
                      'Freezer Image'
                    )
                  "
                  class="me-2 mb-2"
                  v-if="selectedDayDetails.singleRecord.freezer_image"
                >
                  <i class="fa fa-image"></i> View Freezer Image
                </b-button>
                <b-button
                  variant="info"
                  size="sm"
                  @click="
                    viewImageLarge(
                      [selectedDayDetails.singleRecord.chiller_image],
                      'Chiller Image'
                    )
                  "
                  class="mb-2"
                  v-if="selectedDayDetails.singleRecord.chiller_image"
                >
                  <i class="fa fa-image"></i> View Chiller Image
                </b-button>
                <div
                  v-if="
                    !selectedDayDetails.singleRecord.oos_images?.length &&
                    !selectedDayDetails.singleRecord.pre_preparation_images
                      ?.length &&
                    !selectedDayDetails.singleRecord.packaging_images?.length &&
                    !selectedDayDetails.singleRecord.freezer_image &&
                    !selectedDayDetails.singleRecord.chiller_image
                  "
                  class="text-muted"
                >
                  No images available for this record
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Image Modal -->
    <b-modal
      v-model="showImageModal"
      :title="currentImageTitle"
      size="xl"
      hide-footer
    >
      <div v-if="currentImages && typeof currentImages === 'object'">
        <div v-if="currentImages.oos_images && currentImages.oos_images.length">
          <h6>OOS Images</h6>
          <div class="row">
            <div
              class="col-md-4 mb-3"
              v-for="(image, index) in currentImages.oos_images"
              :key="'oos-' + index"
            >
              <div class="image-container">
                <img
                  :src="cdn + '/' + image"
                  :alt="`OOS Image ${index + 1}`"
                  class="img-fluid rounded"
                  style="max-height: 300px; width: 100%; object-fit: cover"
                  @error="$event.target.style.display = 'none'"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="
            currentImages.pre_preparation_images &&
            currentImages.pre_preparation_images.length
          "
        >
          <h6>Pre-preparation Images</h6>
          <div class="row">
            <div
              class="col-md-4 mb-3"
              v-for="(image, index) in currentImages.pre_preparation_images"
              :key="'preprep-' + index"
            >
              <div class="image-container">
                <img
                  :src="cdn + '/' + image"
                  :alt="`Pre-prep Image ${index + 1}`"
                  class="img-fluid rounded"
                  style="max-height: 300px; width: 100%; object-fit: cover"
                  @error="$event.target.style.display = 'none'"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="
            currentImages.packaging_images &&
            currentImages.packaging_images.length
          "
        >
          <h6>Packaging Images</h6>
          <div class="row">
            <div
              class="col-md-4 mb-3"
              v-for="(image, index) in currentImages.packaging_images"
              :key="'packaging-' + index"
            >
              <div class="image-container">
                <img
                  :src="cdn + '/' + image"
                  :alt="`Packaging Image ${index + 1}`"
                  class="img-fluid rounded"
                  style="max-height: 300px; width: 100%; object-fit: cover"
                  @error="$event.target.style.display = 'none'"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="
            currentImages.freezer_image && currentImages.freezer_image.length
          "
        >
          <h6>Freezer Image</h6>
          <div class="row">
            <div
              class="col-md-4 mb-3"
              v-for="(image, index) in currentImages.freezer_image"
              :key="'freezer-' + index"
            >
              <div class="image-container">
                <img
                  :src="cdn + '/' + image"
                  :alt="`Freezer Image ${index + 1}`"
                  class="img-fluid rounded"
                  style="max-height: 300px; width: 100%; object-fit: cover"
                  @error="$event.target.style.display = 'none'"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="
            currentImages.chiller_image && currentImages.chiller_image.length
          "
        >
          <h6>Chiller Image</h6>
          <div class="row">
            <div
              class="col-md-4 mb-3"
              v-for="(image, index) in currentImages.chiller_image"
              :key="'chiller-' + index"
            >
              <div class="image-container">
                <img
                  :src="cdn + '/' + image"
                  :alt="`Chiller Image ${index + 1}`"
                  class="img-fluid rounded"
                  style="max-height: 300px; width: 100%; object-fit: cover"
                  @error="$event.target.style.display = 'none'"
                />
              </div>
            </div>
          </div>
        </div>
        <div v-if="currentImages && typeof currentImages === 'object'">
          <!-- ... image sections ... -->
          <div
            v-if="
              !currentImages.oos_images?.length &&
              !currentImages.pre_preparation_images?.length &&
              !currentImages.packaging_images?.length &&
              !currentImages.freezer_image?.length &&
              !currentImages.chiller_image?.length
            "
            class="text-center py-4"
          >
            <p class="text-muted">No images available</p>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="row">
          <div
            class="col-md-4 mb-3"
            v-for="(image, index) in currentImages"
            :key="index"
          >
            <div class="image-container">
              <img
                :src="cdn + '/' + image"
                :alt="`Image ${index + 1}`"
                class="img-fluid rounded"
                style="max-height: 300px; width: 100%; object-fit: cover"
                @error="$event.target.style.display = 'none'"
              />
            </div>
          </div>
        </div>
        <div v-if="currentImages.length === 0" class="text-center py-4">
          <p class="text-muted">No images available</p>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<style scoped>
/* Calendar View Styles */
.calendar-grid {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.day-header {
  padding: 12px;
  text-align: center;
  font-weight: bold;
  border-right: 1px solid #dee2e6;
}

.day-header:last-child {
  border-right: none;
}

.calendar-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  min-height: 400px;
}

.calendar-day {
  min-height: 80px;
  border-right: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  transition: all 0.2s ease;
}

.calendar-day:nth-child(7n) {
  border-right: none;
}

.calendar-day.clickable {
  cursor: pointer;
}

.calendar-day.clickable:hover {
  background-color: rgba(0, 123, 255, 0.1);
  transform: scale(1.02);
}

.empty-day {
  background-color: #f8f9fa;
}

.day-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.day-number {
  font-weight: bold;
  font-size: 0.8rem;
  margin-bottom: 4px;
}

.day-stats {
  font-size: 1.1rem;
  margin-top: auto;
}

.completion-percentage {
  font-weight: bold;
  margin-bottom: 2px;
}

.record-count {
  font-size: 0.8rem;
  color: #666;
}

/* Completion Status Colors */
.completion-none {
  background-color: #f8f9fa;
  color: #6c757d;
}

.completion-very-low {
  background-color: #f8d7da;
  color: #721c24;
  border-left: 4px solid #dc3545;
}

.completion-low {
  background-color: #fff3cd;
  color: #856404;
  border-left: 4px solid #ffc107;
}

.completion-medium {
  background-color: #d1ecf1;
  color: #0c5460;
  border-left: 4px solid #17a2b8;
}

.completion-high {
  background-color: #d4edda;
  color: #155724;
  border-left: 4px solid #28a745;
}

/* Restaurant Timeline Styles */
.restaurant-timeline {
  max-height: 600px;
  overflow-y: auto;
}

.restaurant-row {
  display: flex;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #dee2e6;
  transition: background-color 0.2s ease;
}

.restaurant-row:hover {
  background-color: #f8f9fa;
}

.restaurant-info {
  width: 200px;
  flex-shrink: 0;
  padding-right: 20px;
}

.restaurant-name {
  margin-bottom: 4px;
  font-size: 1rem;
}

.timeline-circles {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.timeline-circle {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  position: relative;
}

.timeline-circle:hover {
  transform: scale(1.1);
  border-color: #007bff;
}

.circle-date {
  font-size: 0.7rem;
  font-weight: bold;
  text-align: center;
  line-height: 1;
}

/* Circle Completion Colors */
.circle-none {
  background-color: #e9ecef;
  color: #6c757d;
}

.circle-very-low {
  background-color: #dc3545;
  color: white;
}

.circle-low {
  background-color: #ffc107;
  color: #212529;
}

.circle-medium {
  background-color: #17a2b8;
  color: white;
}

.circle-high {
  background-color: #28a745;
  color: white;
}

/* Legend Styles */
.legend-items {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.legend-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #dee2e6;
}

/* Responsive Design */
@media (max-width: 768px) {
  .restaurant-info {
    width: 150px;
  }

  .timeline-circle {
    width: 30px;
    height: 30px;
  }

  .circle-date {
    font-size: 0.6rem;
  }

  .calendar-day {
    min-height: 60px;
    padding: 4px;
  }

  .day-number {
    font-size: 1rem;
  }

  .day-stats {
    font-size: 0.7rem;
  }
}

/* Pagination Styles */
.dataTables_paginate {
  display: flex !important;
}
</style>
